import { useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { formatCents, formatDate } from '../lib/format.js';

const { t } = i18n;

type AccountEntry =
  | {
      id: string;
      type: 'PAYMENT';
      occurredAt: string;
      amountCents: number;
      reference: string | null;
    }
  | {
      id: string;
      type: 'STORAGE_CHARGE';
      occurredAt: string;
      amountCents: number;
      periodFrom: string;
      periodTo: string;
      status: 'DRAFT' | 'PUSHED' | 'FAILED';
      invoiceNumber: string | null;
    };

interface Account {
  currency: string;
  paymentCents: number;
  chargeCents: number;
  balanceCents: number;
  entries: AccountEntry[];
}

export function BillingAccountCard({
  owner,
  ownerId,
  reloadKey,
}: {
  owner: 'customers' | 'partners';
  ownerId: string;
  reloadKey: number;
}) {
  const [account, setAccount] = useState<Account | null>(null);

  useEffect(() => {
    setAccount(null);
    api<Account>(`/${owner}/${ownerId}/account`)
      .then(setAccount)
      .catch(() => setAccount(null));
  }, [owner, ownerId, reloadKey]);

  if (!account) return null;
  const balanceTone =
    account.balanceCents >= 0 ? 'text-green-700 dark:text-green-300' : 'text-red-600';

  return (
    <div className="space-y-3 rounded-md border border-border p-4">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('billing.account.title')}
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">{t('billing.account.help')}</p>
      </div>
      <dl className="grid gap-3 sm:grid-cols-3">
        <Money
          label={t('billing.account.payments')}
          value={account.paymentCents}
          currency={account.currency}
        />
        <Money
          label={t('billing.account.charges')}
          value={account.chargeCents}
          currency={account.currency}
        />
        <Money
          label={
            account.balanceCents >= 0 ? t('billing.account.credit') : t('billing.account.debt')
          }
          value={Math.abs(account.balanceCents)}
          currency={account.currency}
          valueClassName={balanceTone}
        />
      </dl>
      {account.entries.length > 0 ? (
        <div className="overflow-x-auto rounded border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-2 py-1 font-medium">{t('billing.account.date')}</th>
                <th className="px-2 py-1 font-medium">{t('billing.account.entry')}</th>
                <th className="px-2 py-1 font-medium">{t('billing.account.reference')}</th>
                <th className="px-2 py-1 text-right font-medium">{t('billing.account.change')}</th>
              </tr>
            </thead>
            <tbody>
              {account.entries.slice(0, 50).map((entry) => (
                <tr key={`${entry.type}-${entry.id}`} className="border-t border-border">
                  <td className="px-2 py-1">{formatDate(entry.occurredAt)}</td>
                  <td className="px-2 py-1">
                    {entry.type === 'PAYMENT'
                      ? t('billing.account.entry.payment')
                      : t('billing.account.entry.charge')}
                  </td>
                  <td className="px-2 py-1 text-muted-foreground">
                    {entry.type === 'PAYMENT'
                      ? (entry.reference ?? '—')
                      : `${formatDate(entry.periodFrom)} → ${formatDate(entry.periodTo)}`}
                  </td>
                  <td
                    className={`px-2 py-1 text-right tabular-nums ${
                      entry.amountCents >= 0 ? 'text-green-700 dark:text-green-300' : 'text-red-600'
                    }`}
                  >
                    {entry.amountCents >= 0 ? '+' : '−'}
                    {formatCents(Math.abs(entry.amountCents), account.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function Money({
  label,
  value,
  currency,
  valueClassName = '',
}: {
  label: string;
  value: number;
  currency: string;
  valueClassName?: string;
}) {
  return (
    <div className="rounded border border-border bg-muted/30 p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`mt-1 text-lg font-semibold tabular-nums ${valueClassName}`}>
        {formatCents(value, currency)}
      </dd>
    </div>
  );
}
